'use client';

import { useCallback, useEffect, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import Modal, { type IModalRef } from './base';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IModalConfirmOptions {
    /** 弹窗标题 */
    title: string;
    /** 弹窗描述文案 */
    description?: string;
    /** 取消按钮文案，默认 Cancel */
    cancelText?: string;
    /** 确认按钮文案，默认 Confirm */
    confirmText?: string;
    /** 点击取消按钮回调 */
    onCancel?: () => void;
    /** 点击确认按钮回调 */
    onConfirm?: () => void;
}

interface IConfirmDialogProps extends IModalConfirmOptions {
    /** Modal 关闭动画结束后的卸载回调 */
    onAfterClose: () => void;
}

// ─── Confirm UI（Figma: 3499-5248）────────────────────────────────────────────

function ConfirmContent(props: IConfirmDialogProps) {
    const {
        title,
        description,
        cancelText = 'Cancel',
        confirmText = 'Confirm',
        onCancel,
        onConfirm,
        onAfterClose,
    } = props;

    const modalRef = useRef<IModalRef>(null);

    // 挂载完成后自动打开 Modal，触发居中缩放进入动画
    useEffect(() => {
        modalRef.current?.open();
    }, []);

    const dismiss = useCallback(() => {
        modalRef.current?.close();
    }, []);

    const handleModalClose = useCallback(() => {
        onAfterClose();
    }, [onAfterClose]);

    const handleCancel = useCallback(() => {
        onCancel?.();
        dismiss();
    }, [onCancel, dismiss]);

    const handleConfirm = useCallback(() => {
        onConfirm?.();
        dismiss();
    }, [onConfirm, dismiss]);

    return (
        <Modal ref={modalRef} placement='center' maskClosable={false} onClose={handleModalClose}>
            <div className='w-[290px] overflow-hidden rounded-[16px] bg-white'>
                <div className='flex flex-col items-center gap-[8px] px-[20px] pb-[16px] pt-[24px] text-center'>
                    <p className='font-poppins-semibold text-[16px] leading-[22px] text-black'>{title}</p>
                    {description ? <p className='text-[14px] leading-[20px] text-[#444]'>{description}</p> : null}
                </div>
                <div className='flex items-center justify-between px-[20px] pb-[20px] pt-[16px]'>
                    <button
                        type='button'
                        onClick={handleCancel}
                        className='flex-1 text-[14px] leading-[20px] text-[#888]'
                    >
                        {cancelText}
                    </button>
                    <button
                        type='button'
                        onClick={handleConfirm}
                        className='flex-1 font-poppins-medium text-[14px] leading-[20px] text-[#9865ff]'
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

// ─── Imperative API ───────────────────────────────────────────────────────────

let activeRoot: Root | null = null;
let activeContainer: HTMLDivElement | null = null;

function destroyActiveConfirm() {
    activeRoot?.unmount();
    activeRoot = null;
    activeContainer?.remove();
    activeContainer = null;
}

/**
 * 命令式唤起确认弹窗，Modal 内部通过 createPortal 挂载到 document.body。
 *
 * ```ts
 * Modal.confirm({
 *   title: 'Confirm Cancellation',
 *   description: 'Your membership access lasts until the end of this billing period.',
 *   cancelText: 'Cancel',
 *   confirmText: 'Stay Subscribed',
 *   onCancel: () => {},
 *   onConfirm: () => {},
 * });
 * ```
 */
export function confirm(options: IModalConfirmOptions): void {
    if (typeof document === 'undefined') {
        return;
    }

    destroyActiveConfirm();

    const container = document.createElement('div');
    document.body.appendChild(container);
    activeContainer = container;

    const root = createRoot(container);
    activeRoot = root;

    const handleAfterClose = () => {
        destroyActiveConfirm();
    };

    root.render(<ConfirmContent {...options} onAfterClose={handleAfterClose} />);
}
